import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { subscriptionsApi } from '../api/client';

function isAdminFromToken(): boolean {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Boolean(payload.isAdmin);
  } catch {
    return false;
  }
}

/**
 * Only for `/onboarding`. Redirects to dashboard when payment onboarding is already complete.
 * On API failure, stays on onboarding (fail closed).
 */
export const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);

  useEffect(() => {
    if (isAdminFromToken()) {
      setOnboardingComplete(true);
      setChecking(false);
      return;
    }

    let cancelled = false;

    subscriptionsApi
      .getOnboardingStatus()
      .then(status => {
        if (!cancelled) {
          setOnboardingComplete(!status.requiresPaymentSetup);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCheckFailed(true);
          setOnboardingComplete(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  if (onboardingComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  if (checkFailed) {
    return (
      <>
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-900">
          We could not verify your account status. Please add a payment method below to continue.
        </div>
        {children}
      </>
    );
  }

  return <>{children}</>;
};
